import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface IdentificacaoPacienteProps {
  onDataChange?: (data: any) => void;
  initialData?: any;
  atendimentoId?: number;
  pacienteId?: number;
  empresaId?: number;
}

// Função para mapear dados do paciente da API para o formato do formulário
const mapPatientDataToForm = (patientData: any) => {
  // Formatar data de nascimento para o formato do input date (YYYY-MM-DD)
  // Usa parsing manual para evitar problemas de timezone
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return "";
    try {
      // Se já está no formato YYYY-MM-DD, retorna diretamente
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Tenta parsear a data
      let date: Date;
      
      // Se está no formato ISO com timezone, parseia diretamente
      if (dateString.includes('T') || dateString.includes('Z')) {
        date = new Date(dateString);
      } else {
        // Para datas sem timezone, assume formato YYYY-MM-DD ou DD/MM/YYYY
        // Parseia manualmente para evitar problemas de timezone
        const parts = dateString.split(/[-\/]/);
        if (parts.length === 3) {
          // Se o primeiro elemento tem 4 dígitos, é YYYY-MM-DD
          if (parts[0].length === 4) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed
            const day = parseInt(parts[2], 10);
            date = new Date(year, month, day);
          } else {
            // DD/MM/YYYY
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            date = new Date(year, month, day);
          }
        } else {
          date = new Date(dateString);
        }
      }
      
      if (isNaN(date.getTime())) return "";
      
      // Usa métodos locais para evitar problemas de timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  if (!patientData) {
    return {
      nome: "",
      dataNascimento: "",
      profissao: "",
      observacoes: "",
    };
  }

  // Caso 1: Dados vêm do atendimento com estrutura aninhada (atendimento.paciente.pessoa)
  if (patientData.paciente) {
    const paciente = patientData.paciente;
    const pessoa = paciente.pessoa;
    const pessoaFisica = pessoa?.pessoaFisica;
    
    // Buscar data de nascimento em diferentes locais possíveis
    const dataNascimento = pessoaFisica?.data_nascimento || pessoa?.data_nascimento || "";
    
    return {
      nome: pessoa?.nome || "",
      dataNascimento: formatDateForInput(dataNascimento),
      profissao: paciente.profissao || "",
      observacoes: patientData.observacoes || paciente.observacoes || "",
    };
  }

  // Caso 2: Dados vêm diretamente do protocolo (já mapeados)
  if (patientData.nome || patientData.dataNascimento) {
    return {
      nome: patientData.nome || "",
      dataNascimento: formatDateForInput(patientData.dataNascimento || patientData.data_nascimento),
      profissao: patientData.profissao || "",
      observacoes: patientData.observacoes || "",
    };
  }

  // Caso 3: Dados vêm de pessoa diretamente
  if (patientData.pessoa) {
    const pessoa = patientData.pessoa;
    const pessoaFisica = pessoa?.pessoaFisica;
    const dataNascimento = pessoaFisica?.data_nascimento || pessoa?.data_nascimento || "";
    
    return {
      nome: pessoa?.nome || "",
      dataNascimento: formatDateForInput(dataNascimento),
      profissao: patientData.profissao || "",
      observacoes: patientData.observacoes || "",
    };
  }

  // Caso padrão: retornar valores vazios
  return {
    nome: "",
    dataNascimento: "",
    profissao: "",
    observacoes: patientData?.observacoes || "",
  };
};

export const IdentificacaoPaciente = ({ onDataChange, initialData }: IdentificacaoPacienteProps) => {
  const [formData, setFormData] = useState(() => mapPatientDataToForm(initialData));
  const previousMappedDataRef = useRef<string>("");
  const hasInitializedRef = useRef(false);

  // Atualizar dados quando initialData mudar (apenas se realmente mudou)
  useEffect(() => {
    const mappedData = mapPatientDataToForm(initialData);
    const mappedDataString = JSON.stringify(mappedData);
    
    // Se os dados mapeados são os mesmos, não atualiza
    if (mappedDataString === previousMappedDataRef.current && hasInitializedRef.current) {
      return;
    }
    
    // Atualiza o estado apenas se os dados realmente mudaram
    setFormData(mappedData);
    previousMappedDataRef.current = mappedDataString;
    
    // Chama onDataChange apenas na primeira inicialização para evitar loops
    if (!hasInitializedRef.current && (mappedData.nome || mappedData.dataNascimento)) {
      hasInitializedRef.current = true;
      // Usa setTimeout para evitar atualizações durante o render
      setTimeout(() => {
        onDataChange?.(mappedData);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
        <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Este formulário está em modo somente leitura. Os dados do paciente não podem ser editados aqui.
        </p>
      </div>
      
      <div>
        <Label>Nome Completo *</Label>
        <Input 
          placeholder="Nome completo do paciente" 
          value={formData.nome}
          readOnly
          className="bg-muted cursor-not-allowed"
        />
      </div>

      <div>
        <Label>Data de Nascimento *</Label>
        <Input 
          type="date"
          value={formData.dataNascimento}
          readOnly
          className="bg-muted cursor-not-allowed"
        />
      </div>

      <div>
        <Label>Observações</Label>
        <Textarea 
          placeholder="Observações adicionais..." 
          rows={3} 
          value={formData.observacoes}
          readOnly
          className="bg-muted cursor-not-allowed resize-none"
        />
      </div>
    </div>
  );
};

export default IdentificacaoPaciente;
